import { Injectable } from '@angular/core';
import { ResourceVm } from 'qp-ui-interface';
import { removeUndefined } from '../../../shared/utils/EventUtlis';
import { AppEventName, EventName, PlaybackEventName } from '../enums/EventName';
import { CommonEventAttributes, IError } from '../interface/appEvent.interface';
import { NewrelicContentStore } from '../store/newrelic-content.store';
import { NewrelicStore } from '../store/newrelic.store';
import { NewrelicAppStore } from '../store/newrelicApp.store';

declare const newrelic: any;

declare const window: Window &
  typeof globalThis & {
    enableNR: boolean;
  };

@Injectable({
  providedIn: 'root',
})
export class NewrelicService {
  private singleTimeEvent = {
    APP_START: false,
  };
  constructor(
    private readonly newrelicAppStore: NewrelicAppStore,
    private readonly newrelicContentStore: NewrelicContentStore,
    private readonly newrelicStore: NewrelicStore
  ) {}

  get store(): NewrelicStore {
    return this.newrelicStore;
  }

  public sendAppEvent(eventName: AppEventName, attributes?: any): void {
    if (eventName in this.singleTimeEvent) {
      if (this.singleTimeEvent[eventName]) {
        return;
      }
      this.singleTimeEvent[eventName] = true;
    }

    let commonAttributes: CommonEventAttributes =
      this.newrelicAppStore.getCommonAttributes();

    this.sendNewrelicEvent(eventName, {
      ...commonAttributes,
      ...attributes,
    });
  }

  /**
   * Sends a built event to New Relic
   * @param eventName The name of the event
   * @param attributes Custom attributes to attach
   */
  private sendNewrelicEvent(
    eventName: EventName,
    attributes: Record<string, any>
  ): void {
    try {
      const cleanAttr = removeUndefined(attributes);
      // console.log('[Newrelic Event]:', eventName, cleanAttr);
      if (window.enableNR && typeof newrelic !== 'undefined') {
        newrelic.addPageAction(eventName, cleanAttr);
      }
    } catch (err) {
      // console.error('Error sending event to New Relic:', err);
    }
  }

  public sendErrorEvent(
    customAttr: IError,
    resource?: ResourceVm,
    disableErrorEvent: boolean = false
  ): void {
    if (disableErrorEvent) return;

    customAttr.error.errorType = 'APP_ERROR';
    return this.sendNewrelicEvents(
      PlaybackEventName.ERROR,
      resource,
      customAttr.error
    );
  }

  public sendNewrelicEvents(
    eventName: EventName,
    resource?: ResourceVm,
    customAttr?: {}
  ): void {
    try {
      if (resource) {
        this.newrelicContentStore.setContentDetails(resource);
      }
      // X-device-id error is built here the actual payload is NRFactoryEvents...
      let attributes: any = {
        ...this.newrelicStore.getDeviceInfo(),
        ...this.newrelicStore.getEventInfo(),
        ...this.newrelicStore.getUserRegionInfo(),
        ...this.newrelicStore.getApplicationInfo(),
        ...this.newrelicStore.getBrowserInfo(),
        ...this.newrelicStore.getUserInfo(),
        ...this.newrelicStore.getAsnInfo(),
        ...this.newrelicStore.getCustomAttributes(),
        ...this.newrelicStore.getLanguageInfo(),
        ...customAttr,
      };

      // Resource data
      if (this.newrelicContentStore.getContentDetails()) {
        attributes = {
          ...attributes,
          ...this.newrelicContentStore.getChannelInfo(),
          ...this.newrelicContentStore.getTabInfo(),
          ...this.newrelicContentStore.getContainerInfo(),
          ...this.newrelicContentStore.getContentInfo(),
          ...this.newrelicContentStore.getCollectionInfo(),
        };
      }
      this.sendNewrelicEvent(eventName, attributes);
    } catch (e) {
      console.error('Error sending NewRelic event:', e);
      // Wrapping the error with additional context before rethrowing
      throw new Error(`Failed to send NewRelic event: ${e.message}`);
    }
  }
}
